import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
  Alert,
  Paper,
} from "@mui/material";
import { FiCheckCircle, FiEdit2 } from "react-icons/fi";
import { apiHandle } from "../../../config/apiHandle/apiHandle";

const ProContract = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null); // { signed, contract_version, contract_html, signed_at, signed_name }
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  const _fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await apiHandle.get("/pro/contract/status");
      setData(res.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load contract.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    _fetchStatus();
  }, []);

  // Canvas drawing handlers
  const _getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * scaleX,
      y: (t.clientY - rect.top) * scaleY,
    };
  };

  const _start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = _getPos(e);
  };

  const _move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = _getPos(e);
    ctx.strokeStyle = "#0533B7";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };

  const _end = () => {
    drawingRef.current = false;
  };

  const _clearSig = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  };

  const _isCanvasEmpty = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext("2d");
    const px = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < px.length; i += 4) if (px[i] !== 0) return false;
    return true;
  };

  const _handleSign = async () => {
    setError("");
    setSuccess("");
    if (!fullName.trim() || fullName.trim().length < 2) {
      setError("Please type your full legal name.");
      return;
    }
    if (_isCanvasEmpty()) {
      setError("Please draw your signature in the box above.");
      return;
    }
    setSubmitting(true);
    try {
      const sigData = canvasRef.current.toDataURL("image/png");
      const fd = new FormData();
      fd.append("full_name", fullName.trim());
      fd.append("signature_image", sigData);
      await apiHandle.post("/pro/contract/sign", fd);
      setSuccess("Thank you. Your signature has been recorded.");
      await _fetchStatus();
    } catch (e) {
      setError(e?.response?.data?.message || "Could not save signature.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <FiEdit2 size={22} color="#0533B7" />
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#1a1a1a" }}>
          Provider Agreement
        </Typography>
      </Stack>

      {data?.signed ? (
        <Alert
          icon={<FiCheckCircle size={20} />}
          severity="success"
          sx={{ mb: 3, alignItems: "center" }}
        >
          <strong>Signed.</strong> Version {data.contract_version} —
          signed by <strong>{data.signed_name}</strong> on{" "}
          {data.signed_at ? new Date(data.signed_at).toLocaleString() : ""}.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You must sign this agreement before accepting jobs through DoneRightAway.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          maxHeight: 500,
          overflowY: "auto",
          backgroundColor: "#fafafa",
          mb: 3,
        }}
      >
        <Box
          dangerouslySetInnerHTML={{ __html: data?.contract_html || "" }}
          sx={{
            "& h2": { fontSize: 20, marginTop: 0 },
            "& h3": { fontSize: 16, marginTop: 2.5 },
            "& p": { fontSize: 14, lineHeight: 1.6 },
            "& li": { fontSize: 14, lineHeight: 1.6, marginBottom: 0.5 },
          }}
        />
      </Paper>

      {!data?.signed && (
        <Paper
          elevation={0}
          sx={{ p: 3, border: "1px solid #e2e8f0", borderRadius: 2 }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 2 }}>
            Sign here
          </Typography>

          <TextField
            fullWidth
            label="Full legal name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="e.g. Jane M. Smith"
          />

          <Typography sx={{ fontSize: 13, color: "#475569", mb: 1 }}>
            Draw your signature
          </Typography>
          <Box
            sx={{
              border: "1.5px dashed #94a3b8",
              borderRadius: 1.5,
              backgroundColor: "#fff",
              touchAction: "none",
            }}
          >
            <canvas
              ref={canvasRef}
              width={800}
              height={180}
              style={{ width: "100%", height: 180, display: "block", cursor: "crosshair" }}
              onMouseDown={_start}
              onMouseMove={_move}
              onMouseUp={_end}
              onMouseLeave={_end}
              onTouchStart={_start}
              onTouchMove={_move}
              onTouchEnd={_end}
            />
          </Box>
          <Stack direction="row" justifyContent="flex-end" mt={1}>
            <Button onClick={_clearSig} size="small" sx={{ textTransform: "none" }}>
              Clear
            </Button>
          </Stack>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              size="large"
              disabled={submitting}
              onClick={_handleSign}
              sx={{
                backgroundColor: "#0533B7",
                textTransform: "none",
                px: 4,
                py: 1.2,
                "&:hover": { backgroundColor: "#0428a0" },
              }}
            >
              {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Sign & Agree"}
            </Button>
          </Box>

          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 2, textAlign: "center" }}>
            By clicking Sign &amp; Agree, you confirm your electronic signature is binding under the
            Ontario Electronic Commerce Act, 2000.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default ProContract;
